import { useMemo } from "react";

import { getGraphQLErrorsAndNetworkError } from "@/lib/apollo-client/custom-error";

import { useCustomSuspenseQuery } from "@/hooks/use-graphql-client";

import {
	HostRole_Hotel_Forbidden,
	useChillnnApplicationErrorHandler,
} from "@/components/features/error-boundary/hooks";

import { PlanRateMatrix_Query } from "./query";
import { appendUnregisteredPublishedRoomRows } from "../../../append-unregistered-published-room-rows";
import { convertRateMatrixRow } from "../../../convert-rate-matrix-row";
import { getMonthDateRange, getMonthDates } from "../../../get-month-date-range";

import type { RoomDisplayFilterSource } from "../../../build-display-filter-items";

type Props = {
	hotelID: string;
	yearMonth: string;
	adultNum: number;
};

/**
 * 日別料金一覧ページ用に Query.rateMatrix を取る。
 *
 * 取得範囲は在庫ページと同じ「表示月の月初〜月末」。
 * 月切替・人数セレクト変更で variables が変わり再取得する。
 * 部屋フィルタの公開状態は roomList.inSale を使い、プラン行のラベルから推測しない。
 * ダミー部屋はプラン行に出たものだけフィルタへ入れる。部屋単体の未設定行には足さない。
 * 公開中なのに rateMatrix に行が無い通常部屋は、未設定セルの行として足す。
 */
export const useFetchPlanRateMatrix = ({ hotelID, yearMonth, adultNum }: Props) => {
	const { onThrowChillnnApplicationError, onConfirmIsChillnnApplicationRoleCode } =
		useChillnnApplicationErrorHandler();
	const { startDt, endDt } = useMemo(() => getMonthDateRange(yearMonth), [yearMonth]);

	const { data, error } = useCustomSuspenseQuery(PlanRateMatrix_Query, {
		variables: {
			hotelID,
			startDt,
			endDt,
			adultNum,
		},
	});

	/**
	 * rateMatrix は Non-Null（RateMatrix!）。権限エラーでリゾルバーが落ちると data 全体が失われ、
	 * roomList の有無から推測できない。GraphQL errors の code で課金ロール不足を判定する。
	 */
	if (!data?.rateMatrix) {
		if (error) {
			const { graphQLErrors } = getGraphQLErrorsAndNetworkError(error);
			const roleError = graphQLErrors.find((graphQLError) =>
				onConfirmIsChillnnApplicationRoleCode(
					typeof graphQLError.extensions?.code === "string" ? graphQLError.extensions.code : undefined,
				),
			);
			if (roleError) {
				const status =
					typeof roleError.extensions?.chillnnStatusCode === "number"
						? roleError.extensions.chillnnStatusCode
						: 403;
				const statusText =
					typeof roleError.extensions?.code === "string"
						? roleError.extensions.code
						: HostRole_Hotel_Forbidden;
				onThrowChillnnApplicationError({ status, statusText });
			}
		}
		throw new Error("rateMatrix is undefined");
	}

	const maxAdultNum = data.rateMatrix.maxAdultNum;
	const dates = useMemo(() => {
		const headerDates = (data.rateMatrix.headers ?? []).map((header) => header.date);
		return headerDates.length > 0 ? headerDates : getMonthDates(yearMonth);
	}, [data.rateMatrix.headers, yearMonth]);
	const adultNums = useMemo(() => [adultNum], [adultNum]);
	const rooms = useMemo((): RoomDisplayFilterSource[] => {
		const toFilterSource = (room: { roomID: string; name: string; inSale: boolean }): RoomDisplayFilterSource => ({
			roomID: room.roomID,
			roomName: room.name,
			inSale: room.inSale,
		});
		const dummyIDsInTable = new Set<string>();
		const asRoomID = (value: unknown): string => {
			if (typeof value !== "string") {
				throw new Error("roomID が文字列ではない");
			}
			return value;
		};
		for (const item of data.rateMatrix.items ?? []) {
			if (item.isDummyRoom) {
				dummyIDsInTable.add(asRoomID(item.roomID));
			}
		}
		return [
			...(data.roomList.rooms ?? []).map(toFilterSource),
			...(data.roomList.dummy ?? [])
				.filter((room) => dummyIDsInTable.has(asRoomID(room.roomID)))
				.map(toFilterSource),
		];
	}, [data.rateMatrix.items, data.roomList.dummy, data.roomList.rooms]);

	const rows = useMemo(() => {
		const rateRows = (data.rateMatrix.items ?? []).map((row) => convertRateMatrixRow(row, adultNum));
		const publishedStandardRooms = (data.roomList.rooms ?? [])
			.filter((room) => room.inSale)
			.map((room) => ({ roomID: room.roomID, roomName: room.name }));
		return appendUnregisteredPublishedRoomRows({
			rows: rateRows,
			publishedRooms: publishedStandardRooms,
			dates,
			adultNums,
		});
	}, [adultNum, adultNums, data.rateMatrix.items, data.roomList.rooms, dates]);

	return {
		rows,
		rooms,
		maxAdultNum,
		headers: data.rateMatrix.headers,
	};
};
